export const MESSAGE_SUCCESS = 'NODDPAY_CONNECT_SUCCESS';
export const MESSAGE_ERROR = 'NODDPAY_CONNECT_ERROR';

export const OAUTH_ACCESS_TOKEN_KEY = 'noddpay_connect_oauth_access_token';
export const CONNECTION_TOKEN_KEY = 'noddpay_connect_connection_token';
export const CONNECTION_EXPIRES_AT_KEY = 'noddpay_connect_connection_expires_at';

const COMPLETE_ERROR_MESSAGES: Record<string, string> = {
  WALLET_NOT_FOUND:
    'No NoddPay wallet for this account. Complete onboarding in NoddPay first.',
  CONNECTION_EXPIRED: 'Link expired. In dev, click "Start new test connection".',
  CONNECTION_NOT_FOUND:
    'Invalid or expired link. Generate a new connection below (dev) or re-open from the partner.',
};

const GENERIC_SERVER_ERROR =
  'Server error completing connection. Check API logs (often SQS webhook queue in local dev).';

export class ConnectionCompleteError extends Error {
  constructor(
    message: string,
    public readonly code: string
  ) {
    super(message);
    this.name = 'ConnectionCompleteError';
  }
}

export function mapCompleteError(
  body: { error?: string; code?: string },
  status?: number
): string {
  if (body.code && COMPLETE_ERROR_MESSAGES[body.code]) {
    return COMPLETE_ERROR_MESSAGES[body.code];
  }
  if (
    status === 500 &&
    (body.error === 'Internal server error' || !body.error)
  ) {
    return GENERIC_SERVER_ERROR;
  }
  return body.error ?? 'Connection failed';
}

export function isConnectionLinkError(code: string): boolean {
  return code === 'CONNECTION_EXPIRED' || code === 'CONNECTION_NOT_FOUND';
}

export function resolveConnectionToken(): string | null {
  const fromUrl = new URLSearchParams(window.location.search).get('token');
  if (fromUrl) {
    sessionStorage.setItem(CONNECTION_TOKEN_KEY, fromUrl);
    return fromUrl;
  }
  return null;
}

export function storeConnectionToken(connectionToken: string): void {
  sessionStorage.setItem(CONNECTION_TOKEN_KEY, connectionToken);
}

export function storeConnectionExpiresAt(expiresAt: number): void {
  sessionStorage.setItem(CONNECTION_EXPIRES_AT_KEY, String(expiresAt));
}

export function getConnectionExpiresAt(): number | null {
  const raw = sessionStorage.getItem(CONNECTION_EXPIRES_AT_KEY);
  if (!raw) return null;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

export function clearConnectionToken(): void {
  sessionStorage.removeItem(CONNECTION_TOKEN_KEY);
  sessionStorage.removeItem(CONNECTION_EXPIRES_AT_KEY);
}

export function isConnectionExpiredLocally(): boolean {
  const expiresAt = getConnectionExpiresAt();
  if (!expiresAt) return false;
  return Date.now() / 1000 > expiresAt;
}

export function formatConnectionExpiry(expiresAt: number): string {
  return new Date(expiresAt * 1000).toLocaleString();
}

interface InitConnectionResponse {
  data: {
    connectionToken: string;
    connectUrl: string;
    expiresAt: number;
  };
}

export async function startTestConnection(
  partnersApi: string,
  publicKey: string
): Promise<InitConnectionResponse['data']> {
  const response = await fetch(`${partnersApi.replace(/\/$/, '')}/connections/init`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ publicKey }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error ?? `Init failed: ${response.status}`);
  }

  const json = (await response.json()) as InitConnectionResponse;
  storeConnectionToken(json.data.connectionToken);
  storeConnectionExpiresAt(json.data.expiresAt);
  return json.data;
}

export async function completeConnection(
  partnersApi: string,
  connectionToken: string,
  accessToken: string
) {
  const completeRes = await fetch(`${partnersApi.replace(/\/$/, '')}/connections/complete`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ connectionToken }),
  });

  if (!completeRes.ok) {
    const body = await completeRes.json().catch(() => ({}));
    const code = typeof body.code === 'string' ? body.code : 'UNKNOWN';
    throw new ConnectionCompleteError(mapCompleteError(body, completeRes.status), code);
  }

  return completeRes.json();
}

export function postToOpener(type: string, payload: unknown) {
  postToHost(type, payload);
}

export interface EmbedContext {
  isIframe: boolean;
  parentOrigin: string | null;
}

export function resolveEmbedContext(): EmbedContext {
  const params = new URLSearchParams(window.location.search);
  const embed = params.get('embed');
  const parentOrigin = params.get('parent_origin');
  const isIframe = embed === 'iframe' && !!parentOrigin;

  return {
    isIframe,
    parentOrigin: isIframe ? parentOrigin : null,
  };
}

export function postToHost(type: string, payload: unknown) {
  const ctx = resolveEmbedContext();

  if (ctx.isIframe && ctx.parentOrigin) {
    window.parent.postMessage({ type, payload }, ctx.parentOrigin);
    return;
  }

  if (window.opener) {
    window.opener.postMessage({ type, payload }, window.location.origin);
  }
}

export function closeConnectWindow() {
  if (!resolveEmbedContext().isIframe) {
    window.close();
  }
}
